<template>
  <v-navigation-drawer
    :color="color"
    :value="isOpen"
    @input="toggleDrawer"
    v-bind="computedDrawerProps"
  >
    <!-- ===== TOP LINKS ===== -->
    <v-list dense>
      <template v-for="item in filteredTopLinks">
        <!-- Simple link -->
        <v-list-item
          v-if="!item.children"
          :key="`single-${item.title}`"
          class="parent-link dense-list-item"
          :to="item.to"
          @click="onItemClick"
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-list-item-icon v-bind="attrs" v-on="on">
                <v-icon>{{ item.icon }}</v-icon>
              </v-list-item-icon>
            </template>
            <span>{{ $t(item.tooltip) }}</span>
          </v-tooltip>

          <v-list-item-content>
            <v-list-item-title>{{ $t(item.title) }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <!-- Group with children -->
        <v-list-group
          v-else
          :key="`group-${item.title}`"
          no-action
          prepend-icon=""
          append-icon="mdi-chevron-down"
          :value="false"
          class="pl-0 parent-link timeline-group"
        >
          <template v-slot:activator>
            <v-list-item class="px-0 dense-list-item">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-list-item-icon v-bind="attrs" v-on="on">
                    <v-icon>{{ item.icon }}</v-icon>
                  </v-list-item-icon>
                </template>
                <span>{{ $t(item.tooltip) }}</span>
              </v-tooltip>

              <v-list-item-content>
                <v-list-item-title>{{ $t(item.title) }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>

          <div class="v-list-group__items">
            <v-list-item
              v-for="child in item.children"
              :key="`child-${item.title}-${child.title}`"
              :to="child.to"
              class="px-4 ml-2 child-link dense-list-item"
              @click="onItemClick"
            >
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-list-item-icon v-bind="attrs" v-on="on">
                    <v-icon>{{ child.icon }}</v-icon>
                  </v-list-item-icon>
                </template>
                <span>{{ $t(child.tooltip) }}</span>
              </v-tooltip>

              <v-list-item-content>
                <v-list-item-title>{{ $t(child.title) }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </div>
        </v-list-group>
      </template>
    </v-list>

    <!-- ===== BOTTOM LINKS ===== -->
    <template v-slot:append>
      <v-list dense>
        <v-list-item
          v-for="item in filteredBottomLinks"
          :key="item.title"
          :disabled="item.disabled"
          :to="item.to"
          class="dense-list-item"
          @click="onItemClick"
        >
          <v-list-item-icon>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>

          <v-list-item-content>
            <v-list-item-title>{{ $t(item.title) }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </template>
  </v-navigation-drawer>
</template>

<script>
import { getTheme } from "@/plugins/vuetify";

export default {
  name: "DrawerGeneric",
  props: {
    colorKey: { type: String, default: "white" },
    isOpen: { type: Boolean, required: true },
    isSubDrawer: { type: Boolean, default: false },
    topLinks: { type: Array, default: () => [] },
    bottomLinks: { type: Array, default: () => [] }
  },
  computed: {
    color() {
      return getTheme(this)[this.colorKey];
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    computedDrawerProps() {
      // En mobile, TOUJOURS temporary
      if (this.isMobile) {
        return {
          app: true,
          temporary: true,
          dark: this.isSubDrawer ? false : true
        };
      }

      // En desktop
      if (this.isSubDrawer) {
        return {
          app: true,
          clipped: true,
          permanent: true
        };
      } else {
        return {
          app: true,
          temporary: true,
          dark: true
        };
      }
    },
    filteredTopLinks() {
      return this.topLinks.filter(link =>
        link.isAllowed ? link.isAllowed(this) : true
      );
    },
    filteredBottomLinks() {
      return this.bottomLinks.filter(link =>
        link.isAllowed ? link.isAllowed(this) : true
      );
    }
  },
  methods: {
    toggleDrawer(val) {
      if (val !== this.isOpen) this.$emit("toggle", val);
    },
    onItemClick() {
      // Fermer le drawer sur mobile après un clic
      if (this.isMobile) {
        this.$emit("toggle", false);
      }
    }
  }
};
</script>

<style scoped>
.timeline-group .v-list-group__items {
  position: relative;
  margin-left: 0.5rem;
}

.timeline-group .v-list-group__items::before {
  content: "";
  position: absolute;
  left: 1rem;
  top: 0;
  bottom: 0;
  width: 4px;
  background-color: rgba(149, 147, 147, 0.4);
  border-radius: 2px;
}

.child-link .v-list-item-icon {
  min-width: 24px !important;
  margin-right: 4px !important;
}

.child-link {
  padding-left: 1.2rem !important;
}

.child-link .v-list-item-title {
  font-size: 0.95rem;
}

.parent-link .v-list-item-title {
  font-weight: 600;
}

/* Marges icônes par défaut */
.v-application--is-ltr .v-list-item__action:first-child,
.v-application--is-ltr .v-list-item__icon:first-child {
  margin-right: 15px;
}

/* ===== DENSIFIER LES LIGNES ===== */
::v-deep .dense-list-item.v-list-item {
  min-height: 36px;
  padding-top: 2px;
  padding-bottom: 2px;
}

::v-deep .dense-list-item .v-list-item__content {
  padding-top: 0;
  padding-bottom: 0;
}

::v-deep .dense-list-item .v-list-item__title {
  font-size: 0.9rem;
  line-height: 1.1;
}

::v-deep .dense-list-item .v-list-item__icon {
  margin-right: 8px;
}
</style>
